<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">Customers</h1>
        <div class="gl-breadcrumb">
          <router-link to="/" class="gl-breadcrumb-link">Home</router-link>
          <span class="gl-breadcrumb-sep">/</span>
          <span class="gl-breadcrumb-link">Super Admin</span>
          <span class="gl-breadcrumb-sep">/</span>
          <span>Customers</span>
        </div>
      </div>
    </div>

    <!-- Filter bar -->
    <div class="gl-filter-bar">
      <div class="gl-search-wrap">
        <svg class="gl-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input type="text" class="gl-search-input" placeholder="Search by name, phone, email…" v-model="search" @input="debouncedSearch" />
      </div>
      <div class="gl-select-wrap">
        <select v-model="filters.is_active" @change="fetchCustomers" class="gl-select">
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>
      <div class="gl-select-wrap">
        <select v-model="filters.has_wallet_balance" @change="fetchCustomers" class="gl-select">
          <option value="">All Wallets</option>
          <option value="true">With Balance</option>
        </select>
      </div>
    </div>

    <!-- Table card -->
    <div class="gl-card">
      <!-- Skeleton -->
      <template v-if="loading">
        <div class="gl-tbl-skel">
          <div class="gl-tbl-skel-head">
            <div class="gl-skel" style="height:11px;flex:1.6;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;flex:1.2;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:90px;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:52px;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:52px;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:80px;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:56px;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:64px;border-radius:6px"></div>
          </div>
          <div v-for="i in 8" :key="i" class="gl-skel-row">
            <div style="display:flex;align-items:center;gap:12px;flex:1.6">
              <div class="gl-skel" style="width:38px;height:38px;border-radius:50%;flex-shrink:0"></div>
              <div style="flex:1">
                <div class="gl-skel" style="height:12px;width:70%;border-radius:6px;margin-bottom:6px"></div>
                <div class="gl-skel" style="height:18px;width:55px;border-radius:20px"></div>
              </div>
            </div>
            <div style="flex:1.2">
              <div class="gl-skel" style="height:12px;width:80%;border-radius:6px;margin-bottom:6px"></div>
              <div class="gl-skel" style="height:10px;width:50%;border-radius:6px"></div>
            </div>
            <div class="gl-skel" style="width:80px;height:13px;border-radius:6px"></div>
            <div class="gl-skel" style="width:40px;height:13px;border-radius:6px"></div>
            <div class="gl-skel" style="width:36px;height:13px;border-radius:6px"></div>
            <div class="gl-skel" style="width:76px;height:13px;border-radius:6px"></div>
            <div class="gl-skel" style="width:54px;height:20px;border-radius:20px"></div>
            <div class="gl-skel" style="width:60px;height:30px;border-radius:8px"></div>
          </div>
        </div>
      </template>

      <!-- Empty -->
      <div v-else-if="customers.length === 0" class="gl-empty-msg">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="opacity:.3;margin-bottom:10px"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        <div>No customers found</div>
      </div>

      <!-- Desktop table -->
      <div v-else class="gl-tbl-scroll">
        <table class="gl-tbl">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Contact</th>
              <th>Wallet</th>
              <th>Points</th>
              <th>Orders</th>
              <th>Total Spent</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="customer in customers" :key="customer.id">
              <td>
                <div class="gl-cust-cell">
                  <div class="gl-cust-avatar" v-if="customer.avatar">
                    <img :src="customer.avatar" :alt="customer.name" />
                  </div>
                  <div class="gl-cust-avatar gl-cust-initial" v-else>
                    {{ (customer.name || '?').charAt(0).toUpperCase() }}
                  </div>
                  <div>
                    <router-link :to="`/super-admin/customers/${customer.id}`" class="gl-link gl-td-bold">
                      {{ customer.name || 'No Name' }}
                    </router-link>
                    <div style="margin-top:3px">
                      <span v-if="customer.membership_tier" class="gl-tier-badge">{{ customer.membership_tier.name }}</span>
                    </div>
                  </div>
                </div>
              </td>
              <td>
                <div style="font-size:13px;color:#2a3d3a">{{ customer.phone || '—' }}</div>
                <div class="gl-td-muted" style="font-size:11.5px;margin-top:2px">{{ customer.email || '—' }}</div>
              </td>
              <td>
                <span :class="customer.wallet_balance > 0 ? 'gl-wallet-pos' : 'gl-td-muted'">
                  RM {{ formatCurrency(customer.wallet_balance) }}
                </span>
              </td>
              <td class="gl-td-bold">{{ customer.points_balance || 0 }}</td>
              <td class="gl-td-bold">{{ customer.orders_count }}</td>
              <td class="gl-td-bold">RM {{ formatCurrency(customer.total_spent) }}</td>
              <td>
                <span :class="customer.is_active ? 'gl-badge gl-badge-active' : 'gl-badge gl-badge-inactive'">
                  {{ customer.is_active ? 'Active' : 'Inactive' }}
                </span>
                <div v-if="customer.phone_verified_at" class="gl-verified">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  Verified
                </div>
              </td>
              <td>
                <div class="gl-act-group">
                  <router-link :to="`/super-admin/customers/${customer.id}`" class="gl-act-btn gl-act-view" title="View">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  </router-link>
                  <router-link :to="`/super-admin/wallets?customer=${customer.id}`" class="gl-act-btn gl-act-wallet" title="Wallet">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                  </router-link>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile cards -->
      <div v-if="!loading && customers.length" class="gl-mob-list">
        <div v-for="customer in customers" :key="'m' + customer.id" class="gl-mob-card">
          <div class="gl-mob-top">
            <div class="gl-cust-cell">
              <div class="gl-cust-avatar gl-cust-avatar-lg" v-if="customer.avatar">
                <img :src="customer.avatar" :alt="customer.name" />
              </div>
              <div class="gl-cust-avatar gl-cust-avatar-lg gl-cust-initial" v-else>
                {{ (customer.name || '?').charAt(0).toUpperCase() }}
              </div>
              <div style="min-width:0">
                <router-link :to="`/super-admin/customers/${customer.id}`" class="gl-mob-name">
                  {{ customer.name || 'No Name' }}
                </router-link>
                <div class="gl-mob-contact">{{ customer.phone || customer.email || '—' }}</div>
                <div style="margin-top:4px;display:flex;gap:6px;align-items:center;flex-wrap:wrap">
                  <span v-if="customer.membership_tier" class="gl-tier-badge">{{ customer.membership_tier.name }}</span>
                  <span v-if="customer.phone_verified_at" class="gl-verified">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    Verified
                  </span>
                </div>
              </div>
            </div>
            <span :class="customer.is_active ? 'gl-badge gl-badge-active' : 'gl-badge gl-badge-inactive'" style="flex-shrink:0">
              {{ customer.is_active ? 'Active' : 'Inactive' }}
            </span>
          </div>

          <div class="gl-mob-bottom">
            <div class="gl-mob-kv">
              <span class="gl-mob-k">Wallet</span>
              <span class="gl-mob-v" :class="customer.wallet_balance > 0 ? 'gl-wallet-pos' : ''">
                RM {{ formatCurrency(customer.wallet_balance) }}
              </span>
            </div>
            <div class="gl-mob-kv">
              <span class="gl-mob-k">Points</span>
              <span class="gl-mob-v">{{ customer.points_balance || 0 }}</span>
            </div>
            <div class="gl-mob-kv">
              <span class="gl-mob-k">Orders</span>
              <span class="gl-mob-v">{{ customer.orders_count }}</span>
            </div>
            <div class="gl-mob-kv">
              <span class="gl-mob-k">Total Spent</span>
              <span class="gl-mob-v">RM {{ formatCurrency(customer.total_spent) }}</span>
            </div>
          </div>

          <div class="gl-mob-actions">
            <router-link :to="`/super-admin/customers/${customer.id}`" class="gl-btn-view-full">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              View Profile
            </router-link>
            <router-link :to="`/super-admin/wallets?customer=${customer.id}`" class="gl-btn-wallet-full">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
              Wallet
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="!loading && meta.last_page > 1" class="gl-pager">
      <div class="gl-pager-info">Showing {{ (meta.current_page - 1) * meta.per_page + 1 }}–{{ Math.min(meta.current_page * meta.per_page, meta.total) }} of {{ meta.total }}</div>
      <div class="gl-pager-btns">
        <button class="gl-pager-btn" :disabled="meta.current_page === 1" @click="changePage(meta.current_page - 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <button v-for="page in paginationPages" :key="page" class="gl-pager-btn" :class="{ 'gl-pager-btn-active': page === meta.current_page }" @click="changePage(page)">{{ page }}</button>
        <button class="gl-pager-btn" :disabled="meta.current_page === meta.last_page" @click="changePage(meta.current_page + 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { superAdminCustomerApi } from '@/services/superAdminApi';

export default {
  name: 'CustomerList',
  data() {
    return {
      loading: false,
      search: '',
      searchTimeout: null,
      filters: { is_active: '', has_wallet_balance: '' },
      customers: [],
      meta: { current_page: 1, last_page: 1, per_page: 20, total: 0 }
    };
  },
  computed: {
    paginationPages() {
      const pages = [];
      const start = Math.max(1, this.meta.current_page - 2);
      const end = Math.min(this.meta.last_page, this.meta.current_page + 2);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  created() { this.fetchCustomers(); },
  methods: {
    async fetchCustomers() {
      this.loading = true;
      try {
        const params = { page: this.meta.current_page, per_page: this.meta.per_page, search: this.search || undefined };
        if (this.filters.is_active !== '') params.is_active = this.filters.is_active;
        if (this.filters.has_wallet_balance !== '') params.has_wallet_balance = this.filters.has_wallet_balance;
        const response = await superAdminCustomerApi.list(params);
        if (response.data.success) { this.customers = response.data.data; this.meta = response.data.meta; }
      } catch (error) { console.error('Failed to fetch customers:', error); }
      finally { this.loading = false; }
    },
    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => { this.meta.current_page = 1; this.fetchCustomers(); }, 300);
    },
    changePage(page) {
      if (page >= 1 && page <= this.meta.last_page) { this.meta.current_page = page; this.fetchCustomers(); }
    },
    formatCurrency(value) {
      if (!value) return '0.00';
      return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }
};
</script>

<style scoped>
.gl-page { padding: 24px 28px 48px; }
.gl-page-header { margin-bottom: 20px; }
.gl-page-title { font-size: 22px; font-weight: 800; color: #1a2e2b; letter-spacing: -0.4px; margin: 0 0 4px; }
.gl-breadcrumb { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #8a9e9a; }
.gl-breadcrumb-link { color: #900C27; text-decoration: none; }
.gl-breadcrumb-link:hover { text-decoration: underline; }
.gl-breadcrumb-sep { opacity: .5; }

.gl-filter-bar { display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; }
.gl-search-wrap { position: relative; flex: 1; min-width: 200px; }
.gl-search-icon { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: #8a9e9a; pointer-events: none; }
.gl-search-input { width: 100%; padding: 9px 12px 9px 34px; border: 1px solid #e0e7e5; border-radius: 9px; font-size: 13px; color: #2a3d3a; background: #fff; outline: none; transition: border-color .15s; box-sizing: border-box; }
.gl-search-input:focus { border-color: #900C27; }
.gl-select-wrap { min-width: 130px; }
.gl-select { width: 100%; padding: 9px 12px; border: 1px solid #e0e7e5; border-radius: 9px; font-size: 13px; color: #2a3d3a; background: #fff; outline: none; cursor: pointer; }

.gl-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; overflow: hidden; margin-bottom: 16px; }
.gl-tbl-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.gl-tbl { width: 100%; border-collapse: collapse; min-width: 780px; }
.gl-tbl thead th { font-size: 11px; font-weight: 700; color: #8a9e9a; text-transform: uppercase; letter-spacing: .4px; padding: 11px 18px; background: #f8faf9; border-bottom: 1px solid #eef0ef; white-space: nowrap; }
.gl-tbl tbody tr { border-bottom: 1px solid #f5f7f6; transition: background .12s; }
.gl-tbl tbody tr:last-child { border-bottom: none; }
.gl-tbl tbody tr:hover { background: #f9fbfa; }
.gl-tbl tbody td { padding: 12px 18px; font-size: 13px; color: #2a3d3a; }
.gl-td-muted { color: #6b7f7b !important; }
.gl-td-bold { font-weight: 700; color: #1a2e2b !important; }
.gl-link { color: #900C27; font-weight: 600; text-decoration: none; }
.gl-link:hover { text-decoration: underline; }

.gl-cust-cell { display: flex; align-items: center; gap: 10px; }
.gl-cust-avatar { width: 38px; height: 38px; border-radius: 50%; overflow: hidden; flex-shrink: 0; }
.gl-cust-avatar img { width: 100%; height: 100%; object-fit: cover; }
.gl-cust-avatar-lg { width: 44px; height: 44px; }
.gl-cust-initial { background: rgba(144,12,39,.1); color: #900C27; font-size: 15px; font-weight: 800; display: flex; align-items: center; justify-content: center; }
.gl-tier-badge { display: inline-block; font-size: 10.5px; font-weight: 600; padding: 2px 8px; border-radius: 20px; background: rgba(59,130,246,.1); color: #1d4ed8; }
.gl-wallet-pos { color: #16a34a; font-weight: 700; }
.gl-verified { display: flex; align-items: center; gap: 3px; font-size: 11px; color: #16a34a; font-weight: 600; margin-top: 3px; }

.gl-badge { display: inline-block; font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 20px; white-space: nowrap; }
.gl-badge-active   { background: #e0f0ec; color: #1a5248; }
.gl-badge-inactive { background: #fce7e7; color: #dc2626; }

.gl-act-group { display: flex; gap: 6px; }
.gl-act-btn { width: 30px; height: 30px; border-radius: 7px; border: 1px solid; display: flex; align-items: center; justify-content: center; cursor: pointer; background: transparent; transition: all .15s; text-decoration: none; }
.gl-act-view { border-color: #900C27; color: #900C27; }
.gl-act-view:hover { background: rgba(144,12,39,.08); }
.gl-act-wallet { border-color: #16a34a; color: #16a34a; }
.gl-act-wallet:hover { background: rgba(22,163,74,.08); }

.gl-skel { background: linear-gradient(90deg, #f0f2f1 25%, #e5e8e7 50%, #f0f2f1 75%); background-size: 200% 100%; animation: gl-shimmer 1.4s infinite; border-radius: 6px; }
.gl-tbl-skel-head { display: flex; align-items: center; gap: 16px; padding: 12px 18px; background: #f8faf9; border-bottom: 1px solid #eef0ef; }
.gl-skel-row { display: flex; align-items: center; gap: 16px; padding: 14px 18px; border-bottom: 1px solid #f5f7f6; }
@keyframes gl-shimmer { to { background-position: -200% 0; } }

.gl-empty-msg { padding: 48px 0; text-align: center; font-size: 13px; color: #a0b5b0; display: flex; flex-direction: column; align-items: center; }

.gl-mob-list { display: none; }

.gl-pager { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; }
.gl-pager-info { font-size: 12.5px; color: #8a9e9a; }
.gl-pager-btns { display: flex; gap: 4px; }
.gl-pager-btn { min-width: 32px; height: 32px; border: 1px solid #e0e7e5; border-radius: 7px; background: #fff; color: #2a3d3a; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0 6px; transition: all .15s; }
.gl-pager-btn:hover:not(:disabled) { border-color: #900C27; color: #900C27; }
.gl-pager-btn:disabled { opacity: .4; cursor: not-allowed; }
.gl-pager-btn-active { background: #900C27 !important; border-color: #900C27 !important; color: #fff !important; font-weight: 700; }

@media (max-width: 767px) {
  .gl-page { padding: 14px 16px 40px; }
  .gl-page-header { margin-bottom: 14px; }
  .gl-breadcrumb { display: none; }
  .gl-filter-bar { gap: 8px; }
  .gl-search-wrap { flex: 1 1 100%; min-width: 0; }
  .gl-select-wrap { flex: 1 1 calc(50% - 4px); min-width: 0; }

  .gl-tbl-scroll { display: none; }
  .gl-tbl-skel-head { display: none; }
  .gl-mob-list { display: flex; flex-direction: column; }
  .gl-skel-row { padding: 12px 16px; gap: 10px; }

  .gl-mob-card { padding: 14px 16px; border-bottom: 1px solid #f0f3f2; }
  .gl-mob-card:last-child { border-bottom: none; }
  .gl-mob-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 10px; }
  .gl-mob-name { font-size: 14px; font-weight: 700; color: #900C27; text-decoration: none; display: block; }
  .gl-mob-name:hover { text-decoration: underline; }
  .gl-mob-contact { font-size: 12px; color: #6b7f7b; margin-top: 2px; }
  .gl-mob-bottom { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 14px; padding: 10px 0; border-top: 1px solid #f5f7f6; border-bottom: 1px solid #f5f7f6; margin-bottom: 10px; }
  .gl-mob-kv { display: flex; flex-direction: column; gap: 2px; }
  .gl-mob-k { font-size: 10.5px; font-weight: 600; color: #8a9e9a; text-transform: uppercase; letter-spacing: .3px; }
  .gl-mob-v { font-size: 13px; font-weight: 700; color: #1a2e2b; }
  .gl-mob-actions { display: flex; gap: 8px; }
  .gl-btn-view-full { flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 8px 12px; border: 1px solid #900C27; border-radius: 8px; color: #900C27; font-size: 13px; font-weight: 600; text-decoration: none; }
  .gl-btn-wallet-full { flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 8px 12px; border: 1px solid #16a34a; border-radius: 8px; color: #16a34a; font-size: 13px; font-weight: 600; text-decoration: none; }
  .gl-pager { flex-direction: column; align-items: flex-start; gap: 8px; }
}
</style>

<style>
body.dark-only .gl-page-title { color: #e0eded; }
body.dark-only .gl-breadcrumb-link { color: #5a7470; }
body.dark-only .gl-search-input { background: #1e2d2b; border-color: #2a3d3a; color: #c0d4d0; }
body.dark-only .gl-search-input:focus { border-color: #3d9e8c; }
body.dark-only .gl-search-icon { color: #5a7470; }
body.dark-only .gl-select { background: #1e2d2b; border-color: #2a3d3a; color: #c0d4d0; }
body.dark-only .gl-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-tbl thead th { background: #16211f; color: #5a7470; border-bottom-color: #2a3d3a; }
body.dark-only .gl-tbl tbody tr { border-bottom-color: #222f2d; }
body.dark-only .gl-tbl tbody tr:hover { background: #1c2f2b; }
body.dark-only .gl-tbl tbody td { color: #c0d4d0; }
body.dark-only .gl-td-muted { color: #5a7470 !important; }
body.dark-only .gl-td-bold { color: #e0eded !important; }
body.dark-only .gl-cust-initial { background: rgba(144,12,39,.2); }
body.dark-only .gl-skel { background: linear-gradient(90deg, #243432 25%, #2a3d3a 50%, #243432 75%); background-size: 200% 100%; }
body.dark-only .gl-tbl-skel-head { background: #16211f; border-bottom-color: #2a3d3a; }
body.dark-only .gl-skel-row { border-bottom-color: #222f2d; }
body.dark-only .gl-pager-btn { background: #1e2d2b; border-color: #2a3d3a; color: #c0d4d0; }
body.dark-only .gl-pager-info { color: #5a7470; }
body.dark-only .gl-mob-card { border-bottom-color: #222f2d; }
body.dark-only .gl-mob-name { color: #3d9e8c; }
body.dark-only .gl-mob-contact { color: #5a7470; }
body.dark-only .gl-mob-bottom { border-color: #2a3d3a; }
body.dark-only .gl-mob-v { color: #e0eded; }
body.dark-only .gl-empty-msg { color: #5a7470; }
</style>
